import { FileData } from '@/app/interfaces/fileData';
import { SigningResponse } from '@/app/interfaces/signingResponse';
import { inject, Injectable } from '@angular/core';
import { Observable, from, map } from 'rxjs';
import { SettingsService } from './settings.service';
import { Certificate } from '@/app/interfaces/digitalSigning';
import { ElectronErrorHandlerService } from '../../interceptors/electron-error-handler.service';

export interface SaveFileOptions {
  fileBuffer: ArrayBuffer;
  baseDir: string;
  subDir?: string;
  fileName: string;
}

@Injectable({
  providedIn: 'root',
})
export class ElectronWrapperService {
  private readonly settingsService = inject(SettingsService);
  private readonly electronErrorHandler = inject(ElectronErrorHandlerService);

  getTodayFolder(): string {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }

  async saveFileWithStructure(
    options: SaveFileOptions,
  ): Promise<{ success: boolean; path?: string; error?: string }> {
    const { fileBuffer, baseDir, subDir, fileName } = options;
    const datePath = `${baseDir}/${this.getTodayFolder()}`;
    const fullPath = subDir
      ? `${datePath}/${subDir}/${fileName}`
      : `${datePath}/${fileName}`;
    try {
      const dirResult = await this.ensureDirectoryExists(
        subDir ? `${datePath}/${subDir}` : datePath,
      );
      if (dirResult?.success === false || dirResult?.error) {
        const error = new Error(
          dirResult.error || 'Unknown error in ensureDirectoryExists',
        );
        this.electronErrorHandler.handleError(error, 'saveFileWithStructure');
        throw error;
      }
      const saveResult = await this.saveFile(fileBuffer, fullPath);
      if (saveResult?.success === false || saveResult?.error) {
        const error = new Error(
          saveResult.error || 'Unknown error in saveFile',
        );
        this.electronErrorHandler.handleError(error, 'saveFileWithStructure');
        throw error;
      }
      return saveResult;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'saveFileWithStructure');
      throw error;
    }
  }

  async signPDFsWithPin(
    pdfPaths: string[],
    pin: string,
    outputPath: string,
    certId: string,
    signingSlot: number,
  ): Promise<SigningResponse> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.signPDFWithPin(
        pdfPaths,
        pin,
        outputPath,
        certId,
        signingSlot,
      );
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in signPDFsWithPin',
        );
        this.electronErrorHandler.handleError(error, 'signPDFsWithPin');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'signPDFsWithPin');
      throw error;
    }
  }

  async getHwSigningDeviceCertificates(): Promise<Certificate[]> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.getHwSigningDeviceCertificates();
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in getHwSigningDeviceCertificates',
        );
        this.electronErrorHandler.handleError(
          error,
          'getHwSigningDeviceCertificates',
        );
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(
        error,
        'getHwSigningDeviceCertificates',
      );
      throw error;
    }
  }

  async selectFile(
    name: string,
    extensions: string[],
    properties: string[],
  ): Promise<FileData[] | null> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.selectFile(
        name,
        extensions,
        properties,
      );
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in selectFile');
        this.electronErrorHandler.handleError(error, 'selectFile');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'selectFile');
      throw error;
    }
  }

  async readFileAsString(filePath: string): Promise<FileData> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.readFileAsString(filePath);
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in readFileAsString',
        );
        this.electronErrorHandler.handleError(error, 'readFileAsString');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'readFileAsString');
      throw error;
    }
  }

  async readFileAsBase64(filePath: string): Promise<string> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.readFileAsBase64(filePath);
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in readFileAsBase64',
        );
        this.electronErrorHandler.handleError(error, 'readFileAsBase64');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'readFileAsBase64');
      throw error;
    }
  }

  async saveFile(
    fileBuffer: ArrayBuffer,
    filePath: string,
  ): Promise<{ success: boolean; path?: string; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.saveFile(fileBuffer, filePath);
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in saveFile');
        this.electronErrorHandler.handleError(error, 'saveFile');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'saveFile');
      throw error;
    }
  }

  async ensureDirectoryExists(
    dirPath: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.ensureDirectoryExists(dirPath);
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in ensureDirectoryExists',
        );
        this.electronErrorHandler.handleError(error, 'ensureDirectoryExists');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'ensureDirectoryExists');
      throw error;
    }
  }

  async selectDirectory(): Promise<string | null> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.selectDirectory();
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in selectDirectory',
        );
        this.electronErrorHandler.handleError(error, 'selectDirectory');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'selectDirectory');
      throw error;
    }
  }

  async getDocumentsAtPath(
    dirPath: string,
  ): Promise<Array<{ fileName: string; filePath: string }>> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.getPdfDocumentsAtPath(dirPath);
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in getDocumentsAtPath',
        );
        this.electronErrorHandler.handleError(error, 'getDocumentsAtPath');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'getDocumentsAtPath');
      throw error;
    }
  }

  async getDocumentsAtPathNonRecursive(
    dirPath: string,
  ): Promise<Array<{ fileName: string; filePath: string }>> {
    try {
      const result =
        // @ts-expect-error electron
        await window.electronAPI.getPdfDocumentsAtPathNonRecursive(dirPath);
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in getDocumentsAtPathNonRecursive',
        );
        this.electronErrorHandler.handleError(
          error,
          'getDocumentsAtPathNonRecursive',
        );
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(
        error,
        'getDocumentsAtPathNonRecursive',
      );
      throw error;
    }
  }

  async getJsonsAtPath(
    dirPath: string,
  ): Promise<Array<{ fileName: string; filePath: string }>> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.getJsonFilesAtPath(dirPath);
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in getJsonsAtPath',
        );
        this.electronErrorHandler.handleError(error, 'getJsonsAtPath');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'getJsonsAtPath');
      throw error;
    }
  }

  async deleteDirectory(
    dirPath: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.deleteDirectory(dirPath);
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in deleteDirectory');
        this.electronErrorHandler.handleError(error, 'deleteDirectory');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'deleteDirectory');
      throw error;
    }
  }

  async deleteFile(
    filePath: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.deleteFile(filePath);
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in deleteFile');
        this.electronErrorHandler.handleError(error, 'deleteFile');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'deleteFile');
      throw error;
    }
  }

  async fileExists(filePath: string): Promise<boolean> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.fileExists(filePath);
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in fileExists');
        this.electronErrorHandler.handleError(error, 'fileExists');
        throw error;
      }
      return result.exists;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'fileExists');
      throw error;
    }
  }

  async moveFile(
    sourceFilePath: string,
    destinationFilePath: string,
  ): Promise<{ success: boolean; path?: string; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.moveFile(
        sourceFilePath,
        destinationFilePath,
      );
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in moveFile');
        this.electronErrorHandler.handleError(error, 'moveFile');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'moveFile');
      throw error;
    }
  }

  async openFile(
    filePath: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.openFile(filePath);
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'openFile');
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async openDirectory(
    dirPath: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.openDirectory(dirPath);
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'openDirectory');
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async convertHtmlToPdf(
    htmlPath: string,
    pdfPath: string,
  ): Promise<{ success: boolean; path?: string; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.convertHtmlToPdf(
        htmlPath,
        pdfPath,
      );
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in convertHtmlToPdf',
        );
        this.electronErrorHandler.handleError(error, 'convertHtmlToPdf');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'convertHtmlToPdf');
      throw error;
    }
  }

  async logToFile(
    message: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const logFileName = `log-${new Date().toISOString().slice(0, 10)}.log`;
      // @ts-expect-error electron
      const result = await window.electronAPI.writeLog(logFileName, message);
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in moveFile');
        this.electronErrorHandler.handleError(error, 'moveFile');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'moveFile');
      throw error;
    }
  }

  async getAppVersion(): Promise<string> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.getAppVersion();
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in getAppVersion',
        );
        this.electronErrorHandler.handleError(error, 'getAppVersion');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'getAppVersion');
      throw error;
    }
  }

  async copyFile(
    sourcePath: string,
    destinationPath: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.copyFile(
        sourcePath,
        destinationPath,
      );
      if (result?.success === false || result?.error) {
        const error = new Error(result.error || 'Unknown error in copyFile');
        this.electronErrorHandler.handleError(error, 'copyFile');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'copyFile');
      throw error;
    }
  }

  async startPowerSaveBlocker(): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.startPowerSaveBlocker();
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in startPowerSaveBlocker',
        );
        this.electronErrorHandler.handleError(error, 'startPowerSaveBlocker');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'startPowerSaveBlocker');
      throw error;
    }
  }

  async stopPowerSaveBlocker(): Promise<{ success: boolean; error?: string }> {
    try {
      // @ts-expect-error electron
      const result = await window.electronAPI.stopPowerSaveBlocker();
      if (result?.success === false || result?.error) {
        const error = new Error(
          result.error || 'Unknown error in stopPowerSaveBlocker',
        );
        this.electronErrorHandler.handleError(error, 'stopPowerSaveBlocker');
        throw error;
      }
      return result;
    } catch (error) {
      this.electronErrorHandler.handleError(error, 'stopPowerSaveBlocker');
      throw error;
    }
  }

  startClipboardMonitoring(): Observable<void> {
    // @ts-expect-error electron
    return from(window.electronAPI.startClipboardMonitoring()).pipe(map(() => undefined));
  }

  stopClipboardMonitoring(): Observable<void> {
    // @ts-expect-error electron
    return from(window.electronAPI.stopClipboardMonitoring()).pipe(map(() => undefined));
  }

  onClipboardImage(): Observable<string> {
    return new Observable<string>(observer => {
      const handler = (base64: string) => observer.next(base64);
      // @ts-expect-error electron
      window.electronAPI.onClipboardImage(handler);
      // @ts-expect-error electron
      return () => window.electronAPI.offClipboardImage(handler);
    });
  }
}
